/**
 * Lease "Checklists" tab: one card per phase with the landlord's own row (links to the existing
 * runner via the existing `lease-condition-walkthrough-*-link` destinations) and the tenant's row.
 * Always available, including on archived leases.
 */

import { useNavigate } from 'react-router-dom';
import { ChevronRight, ClipboardCheck } from 'lucide-react';
import { asCalendarDateString } from '@resida/shared';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { EmptyState } from '../layout';
import { ChecklistStatusBadge } from './ChecklistStatusBadge';
import { formatCalendarDate } from '../../utils/date';
import type { ChecklistRow, WalkthroughPhase } from '../../lib/condition.api';
import type { UseChecklistsResult } from '../../lib/useChecklists';

interface LeaseChecklistsTabProps {
  leaseId: string;
  archived: boolean;
  startDate: string;
  endDate: string | null;
  checklists: Pick<UseChecklistsResult, 'data' | 'status' | 'retry'>;
  onView: (walkthroughId: string, phase: WalkthroughPhase) => void;
}

const slug = (phase: WalkthroughPhase) => (phase === 'MOVE_IN' ? 'move-in' : 'move-out');

export function LeaseChecklistsTab({ leaseId, archived, startDate, endDate, checklists, onView }: LeaseChecklistsTabProps) {
  const navigate = useNavigate();
  const pad = 'mx-[var(--page-padding-x)] md:mx-4';

  if (checklists.status === 'loading') {
    return (
      <div className={`${pad} flex flex-col gap-3`} data-testid="lease-checklists-loading">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (checklists.status === 'error') {
    return (
      <div className={`${pad} flex flex-col gap-2 rounded-xl border border-border bg-card p-[var(--card-padding)]`} role="status">
        <ChecklistStatusBadge state="ERROR" data-testid="checklist-state-unavailable" />
        <Button type="button" variant="ghost" className="self-start px-1 text-foreground" onClick={checklists.retry} data-testid="checklist-retry">
          Retry
        </Button>
      </div>
    );
  }

  const rows = [...(checklists.data ?? [])].sort((a, b) => (a.phase === b.phase ? 0 : a.phase === 'MOVE_IN' ? -1 : 1));
  if (rows.length === 0) {
    return (
      <EmptyState
        icon={ClipboardCheck}
        title="No checklists for this lease"
        description="Move-in and move-out checklists appear here once they apply to the lease."
        data-testid="lease-checklists-empty"
      />
    );
  }

  const dateLine = (row: ChecklistRow) => {
    const date = row.phase === 'MOVE_IN' ? startDate : endDate ?? row.leaseEndDate;
    if (!date) return null;
    const formatted = formatCalendarDate(asCalendarDateString(date.substring(0, 10)));
    return row.phase === 'MOVE_IN' ? formatted : `Ends ${formatted}`;
  };

  return (
    <div className={`${pad} flex flex-col gap-3`}>
      {rows.map((row) => {
        const phaseSlug = slug(row.phase);
        const phaseLabel = row.phase === 'MOVE_IN' ? 'Move-in' : 'Move-out';
        const hasOwn = row.landlord.state !== 'NOT_STARTED';
        const date = dateLine(row);
        const ownLabel = row.phase === 'MOVE_IN' ? 'Move-In Baseline' : 'Move-Out Walkthrough';
        return (
          <section
            key={row.phase}
            aria-label={phaseLabel}
            data-testid={`lease-checklists-phase-${phaseSlug}`}
            className="flex flex-col gap-3 rounded-xl border border-border bg-card p-[var(--card-padding)]"
          >
            <h3 className="m-0 flex flex-wrap items-center gap-2 text-sm font-semibold text-foreground">
              {phaseLabel}
              {date && <span className="font-normal text-muted-foreground">{date}</span>}
              {archived && <Badge variant="danger">Archived</Badge>}
            </h3>

            {archived && !hasOwn ? (
              <div
                className="flex min-h-[44px] items-center gap-2 rounded-lg border border-dashed border-border p-3"
                data-testid={`lease-condition-walkthrough-${phaseSlug}-not-recorded`}
              >
                <ClipboardCheck className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate text-sm text-muted-foreground">{ownLabel} — not recorded</span>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => navigate(`/leases/${leaseId}/condition-walkthroughs/${phaseSlug}`)}
                className="flex min-h-[44px] w-full items-center justify-between gap-2 rounded-lg border border-border bg-transparent p-3 text-left transition-colors hover:bg-accent"
                data-testid={`lease-condition-walkthrough-${phaseSlug}-link`}
              >
                <div className="flex min-w-0 items-center gap-2">
                  <ClipboardCheck className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="truncate text-sm font-medium text-foreground">
                    {ownLabel}
                    {archived ? ' (record)' : ''}
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <ChecklistStatusBadge state={row.landlord.state} side="landlord" data-testid={`checklist-landlord-state-${phaseSlug}`} />
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </button>
            )}

            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between" data-testid={`lease-checklists-tenant-${phaseSlug}`}>
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-3">
                <span className="text-sm font-medium text-foreground">Tenant&apos;s {phaseSlug} checklist</span>
                <ChecklistStatusBadge
                  state={row.tenant.state}
                  submittedAt={row.tenant.submittedAt}
                  optional={row.tenant.optional}
                  data-testid={`checklist-state-${phaseSlug}`}
                />
              </div>
              {row.tenant.state === 'SUBMITTED' && row.tenant.walkthroughId && (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full text-foreground md:w-auto"
                  onClick={() => onView(row.tenant.walkthroughId as string, row.phase)}
                  data-testid={`checklist-view-${phaseSlug}`}
                >
                  View checklist
                </Button>
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}
